import { useTranslation } from 'react-i18next';
import { eachDayOfInterval, endOfWeek, isSameDay, startOfWeek } from 'date-fns';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';
import { useAssistantTargetCollection } from '@/components/assistant/uiActions/useAssistantTargetCollection';
import { cn } from '@/lib/utils';
import { buildTherapyTypeTranslationKey } from '@/constants/appointmentOptions';
import {
  WEEK_VIEW_HOUR_HEIGHT_PIXELS,
  buildWorkingHoursSequence,
} from '@/constants/calendarConfiguration';
import {
  formatDateToDayOfMonthNumber,
  formatDateToIsoDateOnly,
  formatDateToShortWeekday,
} from '@/utils/dateFormatting';
import { filterAppointmentsForIsoDate } from '../utils/appointmentFiltering';
import {
  assignAppointmentColumns,
  computeAppointmentHeightPixels,
  computeAppointmentTopOffsetPixels,
} from '../utils/appointmentLayout';
import {
  isAppointmentCancelled,
  resolvePatientDisplayName,
} from '../utils/appointmentDisplay';
import { RescheduleRequestMarker } from '../RescheduleRequestMarker';
import { resolveAppointmentColorClasses } from '@/constants/appointmentColors';
import { ExternalEventOverlay } from '../ExternalEventOverlay';
import type { WeekViewProps } from '../types';

const WEEK_STARTS_ON_MONDAY = { weekStartsOn: 1 } as const;

export function WeekView({
  currentDate,
  selectedDate,
  appointments,
  externalEvents,
  onAppointmentSelected,
}: WeekViewProps) {
  const { t } = useTranslation();
  const buildAppointmentTargetRef = useAssistantTargetCollection(
    ASSISTANT_TARGET_KEY.dashboardAppointmentRow
  );

  const weekStart = startOfWeek(currentDate, WEEK_STARTS_ON_MONDAY);
  const weekEnd = endOfWeek(weekStart, WEEK_STARTS_ON_MONDAY);
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });
  const workingHours = buildWorkingHoursSequence();
  const patientFallbackText = t('appointments:patientFallback');

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <p className="px-4 py-2 text-xs text-zoomuted border-b border-zooborder [@container(min-width:45rem)]:hidden">
        {t('calendar:weekSwipeHint')}
      </p>
      <div className="flex-1 overflow-auto custom-scrollbar">
        <div className="min-w-[720px]">
          <div className="flex border-b border-zooborder bg-zoovpalelavender bg-opacity-30 sticky top-0 z-30">
            <div className="w-16 flex-shrink-0 border-r border-zooborder bg-zoovpalelavender bg-opacity-30 sticky left-0 z-10" />
            {weekDays.map(function renderWeekdayHeader(day) {
              const isToday = isSameDay(day, new Date());
              const isSelected = isSameDay(day, selectedDate);

              return (
                <div
                  key={formatDateToIsoDateOnly(day)}
                  className={cn(
                    'flex-1 py-3 text-center border-r border-zooborder',
                    isSelected
                      ? 'bg-zoopalelavender bg-opacity-30 ring-1 ring-inset ring-zooprimary'
                      : ''
                  )}
                >
                  <div className="text-[10px] font-bold text-zoomuted uppercase tracking-wider">
                    {formatDateToShortWeekday(day)}
                  </div>
                  <div
                    className={cn(
                      'text-lg font-bold w-8 h-8 mx-auto mt-1 flex items-center justify-center rounded-full',
                      isToday && isSelected
                        ? 'bg-zooprimary text-zoowhite shadow-sm ring-2 ring-offset-1 ring-zooprimary'
                        : isToday
                          ? 'bg-zoovpalelavender text-zooprimary ring-1 ring-zooprimary'
                          : isSelected
                            ? 'bg-zooprimary text-zoowhite shadow-sm'
                            : 'text-zooindigo'
                    )}
                  >
                    {formatDateToDayOfMonthNumber(day)}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex relative mt-2 mb-4">
            <div className="w-16 flex-shrink-0 border-r border-zooborder bg-zoowhite sticky left-0 z-20">
              {workingHours.map(function renderHourMarker(hour) {
                return (
                  <div key={hour} className="h-20 border-b border-zooborder relative">
                    <span className="absolute -top-2 w-full text-center text-[10px] font-bold text-zoomuted bg-zoowhite">
                      {hour}:00
                    </span>
                  </div>
                );
              })}
            </div>
            {weekDays.map(function renderDayColumn(day) {
            const dayAppointments = filterAppointmentsForIsoDate(
              appointments,
              formatDateToIsoDateOnly(day)
            );
            const columnPlacements = assignAppointmentColumns(dayAppointments);
            return (
              <div
                key={formatDateToIsoDateOnly(day)}
                className="flex-1 border-r border-zooborder relative"
              >
                {workingHours.map(function renderHourCell(hour) {
                  return (
                    <div
                      key={hour}
                      className="h-20 border-b border-zooborder bg-zoowhite bg-opacity-20"
                    />
                  );
                })}
                <ExternalEventOverlay
                  isoDate={formatDateToIsoDateOnly(day)}
                  externalEvents={externalEvents}
                  hourHeightPixels={WEEK_VIEW_HOUR_HEIGHT_PIXELS}
                  isCompact
                />
                {dayAppointments.map(function renderAppointmentCard(appointment) {
                  const topOffsetPixels = computeAppointmentTopOffsetPixels(
                    appointment.startTime,
                    WEEK_VIEW_HOUR_HEIGHT_PIXELS
                  );
                  const heightPixels = computeAppointmentHeightPixels(
                    appointment.startTime,
                    appointment.endTime,
                    WEEK_VIEW_HOUR_HEIGHT_PIXELS
                  );
                  const placement = columnPlacements[appointment.id];
                  const columnCount = placement === undefined ? 1 : placement.columnCount;
                  const columnIndex = placement === undefined ? 0 : placement.columnIndex;
                  const columnWidthPercent = 100 / columnCount;
                  const colorClasses = resolveAppointmentColorClasses(appointment.therapyType);
                  const appointmentPatientName = resolvePatientDisplayName(
                    appointment,
                    patientFallbackText
                  );
                  return (
                    <button
                      key={appointment.id}
                      ref={buildAppointmentTargetRef(appointment.id)}
                      type="button"
                      aria-label={t('calendar:appointmentAriaLabel', {
                        patient: appointmentPatientName,
                        time: appointment.startTime,
                      })}
                      onClick={function editThisAppointment() {
                        onAppointmentSelected(appointment);
                      }}
                      className={cn(
                        'absolute text-left border-l-4 border border-zooborder rounded-lg p-1.5 shadow-sm cursor-pointer hover:border-zooprimary transition-all z-10 overflow-hidden flex flex-col',
                        colorClasses.softBg,
                        colorClasses.accentBorderLeft,
                        isAppointmentCancelled(appointment) ? 'opacity-50 line-through' : ''
                      )}
                      style={{
                        top: `${topOffsetPixels}px`,
                        height: `${heightPixels}px`,
                        minHeight: '24px',
                        left: `calc(${columnIndex * columnWidthPercent}% + 2px)`,
                        width: `calc(${columnWidthPercent}% - 4px)`,
                      }}
                    >
                      <div
                        className={cn(
                          'text-[10px] font-bold leading-tight',
                          colorClasses.accentText
                        )}
                      >
                        {appointment.startTime}
                        {appointment.endTime.length > 0 ? ` - ${appointment.endTime}` : ''}
                      </div>
                      <div className="text-[10px] font-bold text-zooindigo truncate leading-tight mt-0.5 flex items-center gap-1">
                        <span className="truncate">{appointmentPatientName}</span>
                        <RescheduleRequestMarker appointment={appointment} />
                      </div>
                      <div className="text-[9px] text-zoomuted truncate leading-tight">
                        {t(buildTherapyTypeTranslationKey(appointment.therapyType))}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
}
